import {
  Datagrid,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  Pagination,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin'
import { Chip, Link, Typography } from '@mui/material'

import FilterComponent from '../../components/Filter'

import {
  CHAPTER_DISCUSSION_COMMENTS_RESOURCE,
  type DiscussionCommentListRecord,
} from './chapterDiscussions.dataProvider'

export type DiscussionModerationListProps = Record<string, never>

const MODERATION_CHOICES = [
  'queued',
  'approved',
  'rejected',
  'needs_review',
  'error',
  'rate_limited',
  'invalid',
].map(status => ({ id: status, name: status }))

const VISIBILITY_CHOICES = ['pending', 'visible', 'hidden', 'deleted'].map(
  status => ({ id: status, name: status }),
)

const DiscussionPagination = () => (
  <Pagination rowsPerPageOptions={[20, 50, 100]} />
)

const DiscussionModerationFilter = (props: Record<string, unknown>) => (
  <FilterComponent styles={{}} {...props}>
    <SelectInput
      source="moderationStatus"
      label="Moderation"
      choices={MODERATION_CHOICES}
      alwaysOn
    />
    <SelectInput
      source="visibilityStatus"
      label="Visibility"
      choices={VISIBILITY_CHOICES}
      alwaysOn
    />
    <TextInput source="policyReason" label="Policy reason" alwaysOn />
    <NumberInput source="bookId" label="Book ID" alwaysOn />
    <NumberInput source="chapterId" label="Chapter ID" alwaysOn />
    <NumberInput source="authorId" label="Author ID" alwaysOn />
    <TextInput source="exactCommentId" label="Comment ID" alwaysOn />
  </FilterComponent>
)

export function discussionCommentRowClick(
  record: DiscussionCommentListRecord,
): string | false {
  if (window.getSelection()?.toString().trim()) return false
  return `/chapter-discussions/${record.id}/show`
}

export function DiscussionModerationList() {
  return (
    <List
      resource={CHAPTER_DISCUSSION_COMMENTS_RESOURCE}
      title={false}
      actions={false}
      filters={<DiscussionModerationFilter />}
      pagination={<DiscussionPagination />}
      perPage={20}
      sort={{ field: 'createdAt', order: 'ASC' }}
      storeKey="chapter-discussion-comments-list">
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, record) =>
          discussionCommentRowClick(record as DiscussionCommentListRecord)
        }>
        <FunctionField<DiscussionCommentListRecord>
          label="Date"
          source="createdAt"
          sortable={false}
          sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
          render={record => new Date(record.createdAt).toLocaleString()}
        />
        <TextField source="bookTitle" label="Book" sortable={false} />
        <TextField source="chapterTitle" label="Chapter" sortable={false} />
        <FunctionField<DiscussionCommentListRecord>
          label="Comment"
          source="commentBody"
          sortable={false}
          sx={{ maxWidth: 360 }}
          render={record => (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {record.commentBody}
            </Typography>
          )}
        />
        <FunctionField<DiscussionCommentListRecord>
          label="Author"
          source="authorName"
          sortable={false}
          render={record => (
            <Link
              href={`#/user-details/${record.authorId}/show`}
              onClick={event => event.stopPropagation()}>
              {record.authorName}
            </Link>
          )}
        />
        <NumberField source="reportCount" label="Reports" sortable={false} />
        <FunctionField<DiscussionCommentListRecord>
          label="Moderation"
          source="moderationStatus"
          sortable={false}
          render={record => (
            <Chip size="small" label={record.moderationStatus} />
          )}
        />
        <FunctionField<DiscussionCommentListRecord>
          label="Visibility"
          source="visibilityStatus"
          sortable={false}
          render={record => (
            <Chip size="small" label={record.visibilityStatus} />
          )}
        />
      </Datagrid>
    </List>
  )
}
